export const BROKER_BID_BLOCKED_MESSAGE =
  "Brokers are not allowed to bid on this property. Only buyers can place bids here."

export const BROKER_BID_BLOCKED_OTHER_BROKER_MESSAGE =
  "Brokers cannot bid on another broker's property unless it is an exclusive SoldX mandate and broker bidding is enabled on this listing."

export type BrokerBidListingContext = {
  canBrokerBid?: unknown
  isExclusiveMandate?: unknown
  listingCreatorId?: string | null
  listingCreatorRole?: string | null
  currentUserId?: string | null
}

function normalizeYesNo(value: unknown): string {
  return (value ?? "").toString().trim()
}

export function normalizeUserRole(role: unknown): string {
  return (role ?? "").toString().trim().toLowerCase().replace(/\s+/g, "-")
}

export function getAuthUserRole(): string {
  try {
    const raw = localStorage.getItem("authUser")
    if (!raw) return ""
    const obj = JSON.parse(raw) as { role?: string }
    return normalizeUserRole(obj?.role)
  } catch {
    return ""
  }
}

export function getAuthUserId(): string | null {
  try {
    const raw = localStorage.getItem("authUser")
    if (!raw) return null
    const obj = JSON.parse(raw) as Record<string, unknown>
    const v = obj._id ?? obj.uid ?? obj.id ?? obj.userId
    if (v == null) return null
    if (typeof v === "object" && v !== null && "_id" in (v as object)) {
      return String((v as { _id: unknown })._id)
    }
    return String(v)
  } catch {
    return null
  }
}

export function resolveListingCreatorId(createdBy: unknown): string | null {
  if (createdBy == null) return null
  if (typeof createdBy === "string") return createdBy.trim() || null
  if (typeof createdBy === "object" && createdBy !== null) {
    const o = createdBy as Record<string, unknown>
    const id = o._id ?? o.id
    if (id != null) return String(id)
    if (typeof o.$oid === "string") return o.$oid
  }
  const s = String(createdBy).trim()
  return s || null
}

export function resolveListingCreatorRole(createdBy: unknown): string {
  if (createdBy == null || typeof createdBy !== "object") return ""
  const o = createdBy as Record<string, unknown>
  return normalizeUserRole(o.role)
}

function isOtherBrokersListing(ctx: BrokerBidListingContext): boolean {
  const creatorRole = normalizeUserRole(ctx.listingCreatorRole)
  const creatorId = ctx.listingCreatorId?.trim() || null
  const userId = ctx.currentUserId?.trim() || getAuthUserId()
  return (
    creatorRole === "broker" &&
    !!creatorId &&
    !!userId &&
    creatorId !== userId
  )
}

/** True when logged-in broker cannot bid on this listing. */
export function isBrokerBidBlocked(ctx: BrokerBidListingContext): boolean {
  if (getAuthUserRole() !== "broker") return false

  const canBrokerBid = normalizeYesNo(ctx.canBrokerBid)
  const isExclusiveMandate = normalizeYesNo(ctx.isExclusiveMandate)

  if (isOtherBrokersListing(ctx)) {
    return canBrokerBid !== "Yes" || isExclusiveMandate !== "Yes"
  }

  return canBrokerBid !== "Yes"
}

export function getBrokerBidBlockedMessage(ctx: BrokerBidListingContext): string {
  if (isOtherBrokersListing(ctx)) {
    return BROKER_BID_BLOCKED_OTHER_BROKER_MESSAGE
  }
  return BROKER_BID_BLOCKED_MESSAGE
}

export function buildBrokerBidListingContext(input: {
  canBrokerBid?: unknown
  isExclusiveMandate?: unknown
  createdBy?: unknown
  currentUserId?: string | null
}): BrokerBidListingContext {
  return {
    canBrokerBid: input.canBrokerBid,
    isExclusiveMandate: input.isExclusiveMandate,
    listingCreatorId: resolveListingCreatorId(input.createdBy),
    listingCreatorRole: resolveListingCreatorRole(input.createdBy),
    currentUserId: input.currentUserId ?? getAuthUserId(),
  }
}

export function splitClientName(name: string): { first: string; last: string } {
  const trimmed = name.trim()
  if (!trimmed) return { first: "", last: "" }
  const spaceIdx = trimmed.indexOf(" ")
  if (spaceIdx <= 0) return { first: trimmed, last: "" }
  return { first: trimmed.slice(0, spaceIdx), last: trimmed.slice(spaceIdx + 1).trim() }
}
